<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] p-4 pb-24">

    <!-- LOADING if data needs to be loaded -->
    <Loading v-if="loading" :text="$t('views.selectedGoat.loading')" />

    <!-- NOT FOUND if data is not found-->
    <NotFound v-else-if="!goat" :text="$t('views.selectedGoat.notFound')" />

    <!-- Selected Goat Content -->
    <div v-if="goat" class="space-y-6">
      <!-- General Information Module, takes goat,groupname information -->
      <GoatGeneralInfoModule :goat="goat" :groupName="groupName" />

      <!-- Weight Info-->
      <GoatWeightInfoModule v-if="goat" :goatId="goat.id" />

      <!-- Temperature Chart-->
      <GoatTemperatureInfoModule v-if="goat" :goatId="goat.id" />

      <!-- FAMACHA History -->
      <GoatFamachaInfoModule v-if="goat" :goatId="goat.id" />

      <!-- DAG History -->
      <GoatDagInfoModule v-if="goat" :goatId="goat.id" />


      <!-- Health Module -->
      <GoatHealthDetailsInfoModule v-if="goat" :goatId="goat.id" />

      <!-- Notes Module -->
      <GoatNotesModule v-if="goat" :goatId="goat.id" :hasNote="!!goat.notes" />

      <!-- Delete Goat Button -->
      <div>
        <button
          @click="() => { goatToDelete = { ...goat }; showDeleteConfirmation = true }"
          class="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl
                 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50
                 text-red-700 dark:text-red-300 font-semibold shadow
                 active:scale-[0.98] transition select-none border border-red-300 dark:border-red-800">
          <Trash2 class="w-5 h-5" />
          <span>{{ $t('views.goats.deleteGoat') }}</span>
        </button>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <ConfirmAction
      v-if="showDeleteConfirmation && goatToDelete"
      :message="t('views.goats.deleteConfirmMessage', { 
        name: goatToDelete.name || goatToDelete.eartag || (goatToDelete.dateOfBirth ? `${goatToDelete.dateOfBirth} (DOB)` : `ID: ${goatToDelete.id}`)
      })"
      :confirmMessage="$t('common.buttons.delete')"
      variant="danger"
      @confirm="handleDeleteGoat"
      @cancel="showDeleteConfirmation = false" />
  </div>
</template>

<script setup>
  import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { Trash2 } from 'lucide-vue-next'
  import GoatGeneralInfoModule from '@/components/goats/GoatGeneralInfoModule.vue'
  import GoatWeightInfoModule from '@/components/goats/GoatWeightInfoModule.vue'
  import GoatTemperatureInfoModule from '@/components/goats/GoatTemperatureInfoModule.vue'
  import GoatFamachaInfoModule from '@/components/goats/GoatFamachaInfoModule.vue'
  import GoatDagInfoModule from '@/components/goats/GoatDagInfoModule.vue'
  import GoatHealthDetailsInfoModule from '@/components/goats/GoatHealthDetailsInfoModule.vue'
  import GoatNotesModule from '@/components/goats/GoatNotesModule.vue'
  import Loading from '@/components/ui/feedback/Loading.vue'
  import NotFound from '@/components/common/NotFound.vue'
  import ConfirmAction from '@/components/actions/ConfirmAction.vue'
  import { useGoatStore } from '@/stores/goat.store.js'
  import { useGroupStore } from '@/stores/group.store.js'
  import { useWeightStore } from '@/stores/weight.store.js'
  import { useTemperatureStore } from '@/stores/temperature.store.js'
  import { useUiStore } from '@/stores/ui.store'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()

  const route = useRoute()
  const router = useRouter()

  const goatStore = useGoatStore()
  const groupStore = useGroupStore()
  const weightStore = useWeightStore()
  const temperatureStore = useTemperatureStore()
  const ui = useUiStore()

  const loading = ref(true)
  const showDeleteConfirmation = ref(false)
  const goatToDelete = ref(null)
  const deleting = ref(false)
  
  const goatId = computed(() => Number(route.params.id))

  // Reactive computed property that updates when store changes
  const goat = computed(() => {
    return goatStore.goats.find((g) => g.id === goatId.value) || null
  })

  // Fetch selected goat when component is mounted
  onMounted(async () => {
    loading.value = true
    try {
      // Ensure goats are loaded
      if (goatStore.goats.length === 0) {
        await goatStore.fetchGoats()
      }

      // Fetch related data if goat exists
      if (goat.value) {
        weightStore.fetchWeights(goatId.value)
        temperatureStore.fetchTemperatures(goatId.value)
      }
    } catch (error) {
      console.error('Failed to load goat:', error)
    } finally {
      loading.value = false
    }
  })

  watch(goat, (g) => {
    if (!g) return

  //which data should be displayed in Header
  ui.setHeader({
      title: t('views.selectedGoat.title'),
      subtitle: g.eartag || t('views.goats.noEartag'),
      showBack: true,
    })
  }, { immediate: true })
  
  const groupName = computed(() => {
    if (!goat.value) return null
    const g = groupStore.groups.find((x) => x.id === goat.value.groupId)
    return g?.name || '—'
  })

  /**
   * Handle delete goat action
   */
  async function handleDeleteGoat() {
    if (!goatToDelete.value) return

    deleting.value = true
    
    try {
      const success = await goatStore.deleteGoat(goatToDelete.value.id)
      
      if (success) {
        showDeleteConfirmation.value = false
        goatToDelete.value = null
        
        // Navigate back to goat list
        router.push('/goats')
      } else {
        throw new Error('Delete operation failed')
      }
    } catch (error) {
      console.error('Failed to delete goat:', error)
      showDeleteConfirmation.value = false
      goatToDelete.value = null
    } finally {
      deleting.value = false
    }
  }

  // Clean up goatToDelete when dialog is cancelled
  watch(showDeleteConfirmation, (newVal) => {
    if (!newVal) {
      goatToDelete.value = null
    }
  })
</script>
